<template>
  <div id="add-accommodation-review-content">
    <h3>{{ $t('pridanie-recenzie-model-title') }}</h3>
    <form method="post" @submit.prevent="sendReview" v-if="sent === false">


      <div class="star-section">
        <div class="rating">
          <h4>{{ $t('celkove-hodnotenie') }}</h4>
          <div class="stars review">
            <label
                v-for="n in 5"
                :key="n" class="pointer star"
                @mouseover="setHoverRatingForType('total', n)"
                @mouseleave="setHoverRatingForType('total', 0)"
                :class="{'active': review.rating >= n, 'hover': hoverTypeRating['total'] >= n }">
              <TyIcon icon="ty-star" width="2.4rem"/>
              <input type="radio" class="d-none" required v-model="review.rating" :value="n">
            </label>
          </div>

          <a class="btn btn-silver rounded btn-dropdown"
             data-bs-toggle="collapse" @click="showAddReview = !showAddReview"
             :href="`#add_review_additionals`">
            {{ $t(showAddReview ? 'hide-additionals-review' : 'show-additionals-review') }}
            <TyIcon :icon="showAddReview ? 'ty-arrow-up' : 'ty-arrow-down'"></TyIcon>
          </a>

          <div id="add_review_additionals" class="collapse">
            <div class="add_review_additionals">
              <template v-for="code in Object.keys(StarTypes)">
                <div class="review_additional">
                  <div class="title">
                    {{ $t('review-star-type-' + code) }}
                  </div>
                  <div class="stars">
                    <div
                        class="star"
                        v-for="n in 5"
                        :key="n"
                        @click="review.type_rating[code] = n"
                        @mouseover="setHoverRatingForType(code, n)"
                        @mouseleave="setHoverRatingForType(code, 0)"
                        :class="{'active': review.type_rating[code] >= n, 'hover': hoverTypeRating[code] >= n}">
                      <TyIcon icon="ty-star" width="2rem"/>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>


      <div class="write-section">
        <div class="form-group mb-3">
          <label for="review_add_text" class="mb-1 fw-semibold required">{{ $t('napiste-hodnotenie') }}</label>
          <textarea type="text" name="text" v-model="review.text" id="review_add_text" required
                    class="form-control"></textarea>
        </div>

        <div class="form-group mb-3">
          <label for="review_add_recommendation" class="mb-1 fw-semibold">{{ $t('co-by-ste-zlepsili') }}</label>
          <input type="text" name="" v-model="review.recommendation" id="review_add_recommendation"
                 class="form-control">
        </div>

        <div class="form-group mb-3">
          <label for="review_add_location" class="mb-1 fw-semibold">{{ $t('add-review-location-label') }}</label>
          <input type="text" name="" v-model="review.location_tips" id="review_add_location" class="form-control">
        </div>
      </div>


      <div class="visited-with-group">
        <div for="visited_with_group" class="title">
          {{ $t('add-review-visited-with-caption') }}
        </div>
        <template v-for="type in ['solo', 'pair', 'group', 'family-with-small-kids','family-with-large-kids']">
          <label :for="`visited_with_${type}`" class="custom-label">
            <input type="radio" name="review_add_visited" :id="`visited_with_${type}`"
                   v-model="review.visited_as" :value="type" required>
            <span class="title">{{ $t(`visited-with-${type}`) }}</span>
            <span class="checkmark"></span>
          </label>
        </template>
      </div>


      <div class="who-take-it-section" v-if="reservation">
        <p class="h5">{{ $t('Váš pobyt') }}</p>
        <p>

          <span class="black-color">
            {{ $t('from') }}: <strong>{{ formatTime(reservation.date_from, 'DD.MM.YYYY') }}</strong>&nbsp;
            {{ $t('to') }}: <strong>{{ formatTime(reservation.date_to, 'DD.MM.YYYY') }}</strong>
          </span>

        </p>
      </div>

      <div class="who-take-it-section" v-if="review.client !== undefined">
        <div class="form-group mb-3">
          <label for="our_name_and_surname" class="mb-1 fw-semibold required">{{ $t('our-name-and-surname') }}</label>
          <input type="text" name="text" v-model="review.client.client_name" id="our_name_and_surname" required
                 class="form-control">
        </div>

        <div class="form-group mb-3">
          <label for="our_e_mail" class="mb-1 fw-semibold required">{{ $t('our-e-mail') }}</label>
          <input type="email" name="our_e_mail" v-model="review.client.email" id="our_e_mail" class="form-control">
        </div>

        <div class="form-group mb-3">
          <label for="our_phone_number" class="mb-1 fw-semibold required">{{ $t('our-phone-number') }}</label>
          <input type="text" name="our_phone_number" v-model="review.client.phone" id="our_phone_number"
                 class="form-control">
        </div>
      </div>


      <div class="alert alert-danger my-2"
           v-if="tyrionResponseStatus && tyrionResponseStatus.status === 'error'">
        {{ tyrionResponseStatus.msg }}
      </div>


      <div class="form-group text-center">
        <button class="btn btn-first btn-rounded px-5 py-3">
          {{ $t('submit-review-button') }}
        </button>
      </div>


      <PartsTempPreloader v-if="sending"/>
    </form>
    <div class="alert alert-success my-2" v-else-if="sent">
      {{ $t('review-saved-successfully') }}
    </div>
  </div>
</template>

<script setup lang="ts">
import {onMounted, PropType, ref} from 'vue';
import Accommodation from '../../../../../lib/models/Accommodation';
import AccommodationReview from '../../../../../lib/models/AccommodationReview';
import '~/assets/less/add_accommodation_review_content.min.css';
import CsrfToken from '../../../../../lib/models/CsrfToken';
import {tyrionResponse} from '../../../../../composables/tyrionResponse';
import {useNuxtApp} from '#app';
import StarTypes from '~/lib/data/review_rating_start_types.json';
import {getCurrentUser} from "../../../../../composables/auth/useAuthUser";
import Client from "../../../../../lib/models/Client";
import AccommodationReservation from "../../../../../lib/models/AccommodationReservation";
import {formatTime} from "../../../../../lib/functions";

const showAddReview = ref<boolean>(false);
const {$tyrionApi} = useNuxtApp();
const {setTyrionResponseStatus, tyrionResponseStatus} = tyrionResponse();

const props = defineProps({
  accommodation: Object as PropType<Accommodation>,
  activeLocale: {code: String},
  defaultStars: Number | undefined,
  reservation: AccommodationReservation | undefined
});
const loggedClient = getCurrentUser();
const review: AccommodationReview = ref(new AccommodationReview().fromObject({client: (new Client()).fromObject(JSON.parse(JSON.stringify(loggedClient.value)))}));
const sending = ref(false);
const sent = ref(false);
const hoverRating = ref(0);
const hoverTypeRating = ref({});

const setHoverRatingForType = (code: string, n: number) => {
  hoverTypeRating.value[code] = n;
};

onMounted(() => {
  if (props.defaultStars) {
    review.value.rating = props.defaultStars;
  }
});

const sendReview = () => {
  sending.value = true;
  $tyrionApi.getTyrionUrl('/api3/csrf/obtain').then(response => {
    if (response.status === 'success') {
      const csrfToken = new CsrfToken().fromObject(response.data);
      $tyrionApi
          .postTyrionUrl('/api3/accommodation/reviews/create', {
            body: {
              review: review.value,
              id_accommodation: props.accommodation.id,
              id_reservation: props.reservation && props.reservation.id > 0 ? props.reservation.id : -1,
              langCode: props.activeLocale.code,
              csrfToken: {
                key: csrfToken ? csrfToken.key : '',
                value: csrfToken ? csrfToken.value : ''
              }
            },
            userTokenRequired: true
          })
          .then(response => {
            if (response.status === 'success') {
              sent.value = true;
              review.value = new AccommodationReview().fromObject(response.review);
            } else {
              setTyrionResponseStatus('error', response.message);
            }
            sending.value = false;
          });
    } else {
      setTyrionResponseStatus('error', `Unable to send form: ${response.message}`);
      sending.value = false;
    }
  }).catch(e => {
    setTyrionResponseStatus(
        'error',
        e.response && e.response.data && e.response.data.message
            ? e.response.data.message
            : `Error: ${e.message}`
    );
    sending.value = false;
  });
};
</script>
